/**
 * KYC Verification – Level 2 (dashboard-wt theme)
 */
import React, { useState, useEffect } from 'react';
import { useUser } from '../../hooks/useUser';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Button from '../common/Button';
import Input from '../common/Input';
import Select from '../common/Select';
import Spinner from '../common/Spinner';

const inputClass = '!border-[rgba(255,255,255,0.12)] placeholder:!text-[#737373] focus:!border-[#22c55e]';
const inputStyle = { backgroundColor: '#000', color: '#fff' };

const KYCVerification = ({ onProfileUpdated }) => {
  const { requestKYC, loadVerificationStatus, verificationStatus: raw, loading } = useUser();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const status = raw?.data?.verification ?? raw?.verification ?? raw ?? {};
  const [step, setStep] = useState(1);
  const [resubmitting, setResubmitting] = useState(false);
  const [formData, setFormData] = useState({
    idType: 'national_id',
    idNumber: '',
    idFrontPhoto: null,
    idBackPhoto: null,
    selfiePhoto: null,
    addressProofPhoto: null,
    address: '',
    city: '',
    country: 'Cameroon',
  });
  const [errors, setErrors] = useState({});
  const [statusLoading, setStatusLoading] = useState(true);

  const idTypes = [
    { value: 'national_id', label: language === 'fr' ? 'Carte d\'identité' : 'National ID' },
    { value: 'passport', label: language === 'fr' ? 'Passeport' : 'Passport' },
    { value: 'drivers_license', label: language === 'fr' ? 'Permis de conduire' : "Driver's License" },
  ];

  useEffect(() => {
    loadVerificationStatus().catch(() => {});
  }, [loadVerificationStatus]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const handleFileSelect = (field, file) => {
    if (!file) return;
    setFormData((prev) => ({ ...prev, [field]: file }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validateStep1 = () => {
    const e = {};
    if (!formData.idType) e.idType = language === 'fr' ? 'Type requis' : 'Type required';
    if (!formData.idNumber?.trim()) e.idNumber = language === 'fr' ? 'Numéro requis' : 'Number required';
    if (!formData.idFrontPhoto) e.idFrontPhoto = language === 'fr' ? 'Photo recto requise' : 'Front photo required';
    if (!formData.idBackPhoto) e.idBackPhoto = language === 'fr' ? 'Photo verso requise' : 'Back photo required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e = {};
    if (!formData.selfiePhoto) e.selfiePhoto = language === 'fr' ? 'Selfie requise' : 'Selfie required';
    if (!formData.address?.trim()) e.address = language === 'fr' ? 'Adresse requise' : 'Address required';
    if (!formData.city?.trim()) e.city = language === 'fr' ? 'Ville requise' : 'City required';
    if (!formData.country?.trim()) e.country = language === 'fr' ? 'Pays requis' : 'Country required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) handleSubmit();
  };

  const handleSubmit = async () => {
    try {
      const fd = new FormData();
      fd.append('idType', formData.idType);
      fd.append('idNumber', formData.idNumber.trim());
      if (formData.idFrontPhoto && formData.idFrontPhoto instanceof File) {
        fd.append('idFrontPhoto', formData.idFrontPhoto);
      }
      if (formData.idBackPhoto && formData.idBackPhoto instanceof File) {
        fd.append('idBackPhoto', formData.idBackPhoto);
      }
      if (formData.selfiePhoto && formData.selfiePhoto instanceof File) {
        fd.append('selfiePhoto', formData.selfiePhoto);
      }
      if (formData.addressProofPhoto && formData.addressProofPhoto instanceof File) {
        fd.append('addressProofPhoto', formData.addressProofPhoto);
      }
      fd.append('address', formData.address.trim());
      fd.append('city', formData.city.trim());
      fd.append('country', formData.country.trim());
      await requestKYC(fd);
      showSuccess(language === 'fr' ? 'KYC soumis. Examen sous 24–48 h.' : 'KYC submitted. Review in 24–48h.');
      await loadVerificationStatus();
      onProfileUpdated?.();
    } catch (err) {
      showError(err?.message || (language === 'fr' ? 'Échec de la soumission' : 'Submit failed'));
    }
  };

  const FileZone = ({ field, label, accept = 'image/*' }) => {
    const file = formData[field];
    const hasFile = file && (file instanceof File || typeof file === 'object');
    const fileName = file instanceof File ? file.name : null;
    return (
      <label className={`block rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
        hasFile ? 'border-[#22c55e] bg-[rgba(34,197,94,0.08)]' : errors[field] ? 'border-red-500/50' : 'border-[rgba(255,255,255,0.15)] hover:border-[rgba(34,197,94,0.5)]'
      }`}>
        <input
          type="file"
          accept={accept}
          className="hidden"
          onChange={(e) => handleFileSelect(field, e.target.files?.[0])}
        />
        {hasFile ? (
          <span className="text-[#22c55e] font-medium">✓ {fileName || (language === 'fr' ? 'Fichier choisi' : 'File chosen')}</span>
        ) : (
          <span className="text-[#a3a3a3] text-sm">{label}</span>
        )}
        {errors[field] && <p className="text-red-400 text-xs mt-1">{errors[field]}</p>}
      </label>
    );
  };

  // Loading verification status
  if (statusLoading && !hasStatus) {
    return (
      <div className="text-center py-8 px-4">
        <Spinner size="lg" className="mx-auto mb-4" />
        <p className="text-[#a3a3a3] text-sm">{language === 'fr' ? 'Chargement du statut KYC...' : 'Loading KYC status...'}</p>
      </div>
    );
  }

  // Approved
  if (status?.status === 'approved' && !resubmitting) {
    return (
      <div className="text-center py-8 px-4">
        <div className="w-16 h-16 rounded-2xl bg-[rgba(34,197,94,0.2)] border border-[rgba(34,197,94,0.4)] flex items-center justify-center text-3xl mx-auto mb-4">✓</div>
        <h3 className="text-xl font-bold text-[#fafafa] mb-2">{language === 'fr' ? 'KYC vérifié' : 'KYC verified'}</h3>
        <p className="text-[#a3a3a3] text-sm">{language === 'fr' ? 'Accès complet actif.' : 'Full access active.'}</p>
      </div>
    );
  }

  // Pending
  if (status?.status === 'pending' && !resubmitting) {
    return (
      <div className="text-center py-8 px-4">
        <Spinner size="lg" className="mx-auto mb-4" />
        <h3 className="text-xl font-bold text-[#fafafa] mb-2">{language === 'fr' ? 'En cours d\'examen' : 'Under review'}</h3>
        <p className="text-[#a3a3a3] text-sm mb-2">{language === 'fr' ? 'Examen sous 24–48 h.' : 'Review in 24–48h.'}</p>
        {status.submittedAt && <p className="text-[#737373] text-xs">{new Date(status.submittedAt).toLocaleDateString()}</p>}
      </div>
    );
  }

  // Rejected
  if (status?.status === 'rejected' && !resubmitting) {
    return (
      <div className="text-center py-8 px-4">
        <div className="w-16 h-16 rounded-2xl bg-red-500/20 border border-red-500/40 flex items-center justify-center text-2xl mx-auto mb-4">⚠</div>
        <h3 className="text-xl font-bold text-[#fafafa] mb-2">{language === 'fr' ? 'Rejeté' : 'Rejected'}</h3>
        {status.rejectionReason && <p className="text-[#a3a3a3] text-sm mb-4 max-w-md mx-auto">{status.rejectionReason}</p>}
        <button type="button" onClick={() => { setStep(1); setResubmitting(true); }} className="dashboard-wt-btn-primary py-3 px-6">
          {language === 'fr' ? 'Resoumettre' : 'Resubmit'} →
        </button>
      </div>
    );
  }

  // Form: Step 1
  if (step === 1) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 text-sm text-[#a3a3a3] mb-4">
          <span className="w-8 h-8 rounded-full bg-[#22c55e] text-black flex items-center justify-center text-sm font-bold">1</span>
          <span>{language === 'fr' ? 'Documents d\'identité' : 'ID documents'}</span>
        </div>
        <Select
          label={language === 'fr' ? 'Type de pièce' : 'ID type'}
          value={formData.idType}
          onChange={(e) => handleChange('idType', e.target.value)}
          options={idTypes}
          error={errors.idType}
          className={inputClass}
          style={inputStyle}
        />
        <Input
          label={language === 'fr' ? 'Numéro' : 'ID number'}
          value={formData.idNumber}
          onChange={(e) => handleChange('idNumber', e.target.value)}
          error={errors.idNumber}
          placeholder="..."
          className={inputClass}
          style={inputStyle}
        />
        <div className="grid grid-cols-2 gap-4">
          <FileZone field="idFrontPhoto" label={language === 'fr' ? 'Recto' : 'Front'} />
          <FileZone field="idBackPhoto" label={language === 'fr' ? 'Verso' : 'Back'} />
        </div>
        <button type="button" onClick={handleNext} className="dashboard-wt-btn-primary w-full py-3">
          {language === 'fr' ? 'Suivant' : 'Next'} →
        </button>
      </div>
    );
  }

  // Form: Step 2
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-[#a3a3a3] mb-4">
        <span className="w-8 h-8 rounded-full bg-[#22c55e] text-black flex items-center justify-center text-sm font-bold">2</span>
        <span>{language === 'fr' ? 'Selfie & adresse' : 'Selfie & address'}</span>
      </div>
      <FileZone field="selfiePhoto" label={language === 'fr' ? 'Selfie avec pièce' : 'Selfie with ID'} />
      <Input
        label={language === 'fr' ? 'Adresse' : 'Address'}
        value={formData.address}
        onChange={(e) => handleChange('address', e.target.value)}
        error={errors.address}
        placeholder={language === 'fr' ? 'Adresse complète' : 'Full address'}
        className={inputClass}
        style={inputStyle}
      />
      <div className="grid grid-cols-2 gap-4">
        <Input
          label={language === 'fr' ? 'Ville' : 'City'}
          value={formData.city}
          onChange={(e) => handleChange('city', e.target.value)}
          error={errors.city}
          placeholder="Douala"
          className={inputClass}
          style={inputStyle}
        />
        <Input
          label={language === 'fr' ? 'Pays' : 'Country'}
          value={formData.country}
          onChange={(e) => handleChange('country', e.target.value)}
          error={errors.country}
          className={inputClass}
          style={inputStyle}
        />
      </div>
      <FileZone field="addressProofPhoto" label={language === 'fr' ? 'Preuve d\'adresse (optionnel)' : 'Address proof (optional)'} accept="image/*,.pdf" />
      <div className="flex gap-3">
        <button type="button" onClick={() => setStep(1)} className="dashboard-wt-btn-secondary flex-1 py-3">← {language === 'fr' ? 'Retour' : 'Back'}</button>
        <button type="button" onClick={handleNext} disabled={loading} className="dashboard-wt-btn-primary flex-1 py-3">
          {loading ? (language === 'fr' ? 'Envoi...' : 'Submitting...') : (language === 'fr' ? 'Soumettre' : 'Submit')} →
        </button>
      </div>
    </div>
  );
};

export default KYCVerification;
